import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon
} from '@mui/icons-material';
import api from '../../utils/api';

interface Counsellor {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  applicationStatus: string;
  trustIdVerification?: {
    verified: boolean;
    verifiedDate?: string;
    verifiedBy?: string;
  };
  application?: {
    referee?: {
      firstReferee?: {
        name?: string;
        referenceSubmitted?: boolean;
      };
      secondReferee?: {
        name?: string;
        referenceSubmitted?: boolean;
      };
    };
  };
  legalFormSubmitted?: boolean;
  createdAt?: string;
}

const OnboardingManagement: React.FC = () => {
  const navigate = useNavigate();
  const [counsellors, setCounsellors] = useState<Counsellor[]>([]);
  const [filteredCounsellors, setFilteredCounsellors] = useState<Counsellor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  useEffect(() => {
    fetchOnboardingCounsellors();
  }, []);

  useEffect(() => {
    // Filter when search query changes
    handleSearch();
  }, [searchQuery, counsellors]);

  const fetchOnboardingCounsellors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/counsellors');
      
      // Filter counsellors in the post-acceptance onboarding stages
      // (canonical + legacy spellings)
      const onboardingStatuses = new Set([
        'Accepted',
        'Ready For Onboarding',
        'Legal Information Requested',
        'Legal Information Submitted',
        'Legal Info Requested',
        'Legal Info Update Requested',
        'Legal Info Submitted',
        'Pending Self-Completion',
        'Onboarding Review'
      ]);
      const onboardingCounsellors = response.data
        .filter((counsellor: Counsellor) => onboardingStatuses.has(counsellor.applicationStatus))
        .sort((a: Counsellor, b: Counsellor) => {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA; // Most recent first
        });
      
      setCounsellors(onboardingCounsellors);
      setFilteredCounsellors(onboardingCounsellors);
    } catch (err: any) {
      console.error('Error fetching onboarding counsellors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const query = searchQuery.toLowerCase();
    const filtered = counsellors.filter(
      (counsellor) =>
        `${counsellor.firstName} ${counsellor.lastName}`.toLowerCase().includes(query) ||
        counsellor.email.toLowerCase().includes(query)
    );
    setFilteredCounsellors(filtered);
    setCurrentPage(1);
  };

  const handleRowClick = (counsellorId: string) => {
    navigate(`/counsellors/${counsellorId}`);
  };

  const getTrustStatus = (counsellor: Counsellor) => {
    if (counsellor.trustIdVerification?.verified) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Verified"
          size="small"
          sx={{
            backgroundColor: 'rgba(76, 175, 80, 0.3)',
            color: '#66bb6a',
            borderRadius: '12px',
            height: '24px',
            fontSize: '0.75rem',
            fontWeight: 600,
            '& .MuiChip-icon': {
              color: '#66bb6a'
            }
          }}
        />
      );
    }
    return (
      <Chip
        icon={<CancelIcon />}
        label="Unverified"
        size="small"
        sx={{
          backgroundColor: 'rgba(244, 67, 54, 0.3)',
          color: '#ef5350',
          borderRadius: '12px',
          height: '24px',
          fontSize: '0.75rem',
          fontWeight: 600,
          '& .MuiChip-icon': {
            color: '#ef5350'
          }
        }}
      />
    );
  };

  const getReferencesStatus = (counsellor: Counsellor) => {
    const firstRef = counsellor.application?.referee?.firstReferee?.referenceSubmitted || false;
    const secondRef = counsellor.application?.referee?.secondReferee?.referenceSubmitted || false;
    const received = (firstRef ? 1 : 0) + (secondRef ? 1 : 0);
    const total = 2;
    const isComplete = received >= total;

    return (
      <Chip
        icon={isComplete ? <CheckCircleIcon /> : <HourglassEmptyIcon />}
        label={`${received}/${total}`}
        size="small"
        sx={{
          backgroundColor: isComplete ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 152, 0, 0.3)',
          color: isComplete ? '#66bb6a' : '#ffb74d',
          borderRadius: '12px',
          height: '24px',
          fontSize: '0.75rem',
          fontWeight: 600,
          '& .MuiChip-icon': {
            color: isComplete ? '#66bb6a' : '#ffb74d'
          }
        }}
      />
    );
  };

  const getLegalsStatus = (counsellor: Counsellor) => {
    if (counsellor.legalFormSubmitted) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Completed"
          size="small"
          sx={{
            backgroundColor: 'rgba(76, 175, 80, 0.3)',
            color: '#66bb6a',
            borderRadius: '12px',
            height: '24px',
            fontSize: '0.75rem',
            fontWeight: 600,
            '& .MuiChip-icon': {
              color: '#66bb6a'
            }
          }}
        />
      );
    }
    return (
      <Chip
        icon={<CancelIcon />}
        label="Incomplete"
        size="small"
        sx={{
          backgroundColor: 'rgba(244, 67, 54, 0.3)',
          color: '#ef5350',
          borderRadius: '12px',
          height: '24px',
          fontSize: '0.75rem',
          fontWeight: 600,
          '& .MuiChip-icon': {
            color: '#ef5350'
          }
        }}
      />
    );
  };

  // Pagination
  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return {
      paginatedData: filteredCounsellors.slice(startIndex, endIndex),
      totalCount: filteredCounsellors.length
    };
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredCounsellors.length / rowsPerPage)));
  };

  // Summary stats
  const trustVerifiedCount = filteredCounsellors.filter(c => c.trustIdVerification?.verified).length;
  const referencesCompleteCount = filteredCounsellors.filter(c => {
    const firstRef = c.application?.referee?.firstReferee?.referenceSubmitted || false;
    const secondRef = c.application?.referee?.secondReferee?.referenceSubmitted || false;
    return firstRef && secondRef;
  }).length;
  const legalsCompleteCount = filteredCounsellors.filter(c => c.legalFormSubmitted).length;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Info Banner */}
      <div className="mb-6 p-4 glass-card-light border-l-4 border-blue-400">
        <p className="text-sm theme-text-primary">
          <strong>Onboarding Progress Tracker</strong> - Monitor counsellors at the "Accepted", "Ready for Onboarding", "Legal Info Submitted" or "Onboarding Review" stage. 
          Click on any row to view full counsellor details.
        </p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-3 glass-card-light theme-text-primary placeholder-gray-400 border border-white/10 rounded-lg focus:outline-none focus:border-blue-400 transition-colors"
        />
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="glass-card p-4">
          <div className="text-3xl font-bold text-blue-400 mb-1">{filteredCounsellors.length}</div>
          <div className="text-sm theme-text-secondary">Total in Onboarding</div>
        </div>
        <div className="glass-card p-4">
          <div className="text-3xl font-bold text-green-400 mb-1">{trustVerifiedCount}</div>
          <div className="text-sm theme-text-secondary">Trust Verified</div>
        </div>
        <div className="glass-card p-4">
          <div className="text-3xl font-bold text-green-400 mb-1">{referencesCompleteCount}</div>
          <div className="text-sm theme-text-secondary">References Complete</div>
        </div>
        <div className="glass-card p-4">
          <div className="text-3xl font-bold text-green-400 mb-1">{legalsCompleteCount}</div>
          <div className="text-sm theme-text-secondary">Legals Complete</div>
        </div>
      </div>

      {/* Counsellors Table */}
      {filteredCounsellors.length === 0 ? (
        <div className="glass-card p-6 text-center">
          <p className="theme-text-secondary">No counsellors currently in the onboarding stage.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto glass-card">
            <table className="min-w-full divide-y divide-white/10">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-bold theme-text-primary uppercase tracking-wider">
                    Trust
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-bold theme-text-primary uppercase tracking-wider">
                    References
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-bold theme-text-primary uppercase tracking-wider">
                    Legals
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {getPaginatedData().paginatedData.map((counsellor) => (
                  <tr
                    key={counsellor._id}
                    onClick={() => handleRowClick(counsellor._id)}
                    className="hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium theme-text-primary">
                        {counsellor.firstName} {counsellor.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm theme-text-secondary">
                        {counsellor.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getTrustStatus(counsellor)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getReferencesStatus(counsellor)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getLegalsStatus(counsellor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4 px-6 py-3 glass-card-light border-t border-white/10">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md ${
                  currentPage === 1
                    ? 'bg-white/5 text-white/40'
                    : 'bg-white/10 theme-text-primary hover:bg-white/20'
                }`}
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === Math.ceil(filteredCounsellors.length / rowsPerPage)}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md ${
                  currentPage === Math.ceil(filteredCounsellors.length / rowsPerPage)
                    ? 'bg-white/5 text-white/40'
                    : 'bg-white/10 theme-text-primary hover:bg-white/20'
                }`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm theme-text-secondary">
                  Showing <span className="font-medium">{(currentPage - 1) * rowsPerPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * rowsPerPage, filteredCounsellors.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredCounsellors.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-l-md ${
                      currentPage === 1
                        ? 'bg-white/5 text-white/40'
                        : 'bg-white/10 theme-text-primary hover:bg-white/20'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === Math.ceil(filteredCounsellors.length / rowsPerPage)}
                    className={`relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-r-md ${
                      currentPage === Math.ceil(filteredCounsellors.length / rowsPerPage)
                        ? 'bg-white/5 text-white/40'
                        : 'bg-white/10 theme-text-primary hover:bg-white/20'
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OnboardingManagement;
